import { FormStoreState } from "@/shared/interfaces/Form.interface";
import { Criterion } from "@/shared/interfaces/Criterion.interface";
import { deleteValueByPath, getErrorObjectByPath, getValueByPath, setValueByPath } from "@/shared/scripts/ValuesByPath";
import { ApiErrorWrapper } from "@/shared/interfaces/ApiErrorWrapper";
import { Publication } from "@/shared/interfaces/classified/Publication.interface";
import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { RootState } from "../../store.interface";
import { getSteps, StepsConfig } from "@/encoding/scripts/steps";
import { ApiError } from "@/shared/interfaces/ApiError";
import { get } from "lodash";
import { hasWarningsInCurrentStep } from "@/shared/scripts/ValidateData";
import { formatDateDMY } from "@/shared/scripts/formatDateDMY";

const state = {
    steps: getSteps() as StepsConfig,
    currentLocale: "" as string,
    criteria: {},
    criteriaTransaction: {},
    criteriaOrder: {},
    criteriaUI: {},
    classifiedId: null,
    errors: null,
    isEditionMode: false,
    firstPublicationDate: null,
    lastSave: null,
    publication: {} as Publication,
    copyFromId: null,
    validated: false,
};

const mutations: MutationTree<FormStoreState> =  {
    resetCriteria: function(state): void {
        const propertyType = getValueByPath(state.criteria, "property.type");
        const propertySubtype = getValueByPath(state.criteria, "property.subtype");
        const transactionType = getValueByPath(state.criteria, "transaction.type");
        const salePrice = getValueByPath(state.criteria, "transaction.sale.price");
        state.criteria = {};
        state.criteriaTransaction = {},
        state.criteriaOrder = {},
        state.criteriaUI = {},
        state.classifiedId = null;
        state.lastSave = null;
        state.firstPublicationDate = null;
        state.isEditionMode = false;
        state.copyFromId = null;
        state.validated = false;
        if (salePrice) {
            // Remove the 'price' property from 'transaction.sale'
            const transactionSale = getValueByPath(state.criteria, "transaction.sale");
            if (transactionSale && typeof transactionSale === "object") {
                delete transactionSale.price;
            }
        }
        if(propertyType) {
            setValueByPath(state.criteria, "property.type", propertyType);
        }
        if(propertySubtype) {
            setValueByPath(state.criteria, "property.subtype", propertySubtype);
        }
        if(transactionType) {
            setValueByPath(state.criteria, "transaction.type", transactionType);
            setValueByPath(state.criteria, "transaction.subtype", transactionType === "FOR_SALE" ? "BUY_REGULAR" : "RENT_REGULAR");
        }
    },
    setErrors: function(state: FormStoreState, errors: ApiErrorWrapper): void {
        state.errors = errors;
    },
    setErrorsWarning: function(state: FormStoreState, warnings: Array<ApiError>): void {
        if(state.errors) {
            state.errors.warnings = warnings;
        }
    },
    setClassifiedId: function(state: FormStoreState, classifiedId: number): void {
        state.classifiedId = classifiedId;
    },
    setFirstPublicationDate: function(state: FormStoreState, firstPublicationDate: string): void {
        state.firstPublicationDate = firstPublicationDate;
    },
    setLastSave: function(state: FormStoreState, lastSave: string): void {
        state.lastSave = lastSave;
    },
    setIsEditionMode: function(state: FormStoreState, value: boolean): void {
        state.isEditionMode = value;
    },
    setCriteria: function(state: FormStoreState, criteria: Record<any, any>): void {
        state.criteria = criteria;
    },
    addValue: function(state: FormStoreState, criterion: Criterion): void {
        setValueByPath(state.criteria, criterion.path, criterion.value);
    },
    deleteValue: function(state: FormStoreState, pathString: string): void {
        deleteValueByPath(state.criteria, pathString);
    },
    addValueUI: function(state: FormStoreState, criterion: Criterion): void {
        setValueByPath(state.criteriaUI, criterion.path, criterion.value);
    },
    deleteValueUI: function(state: FormStoreState, pathString: string): void {
        deleteValueByPath(state.criteriaUI, pathString);
    },
    addValueTransaction: function(state: FormStoreState, criterion: Criterion): void {
        setValueByPath(state.criteriaTransaction, criterion.path, criterion.value);
    },
    addValueOrder: function(state: FormStoreState, criterion: Criterion): void {
        setValueByPath(state.criteriaOrder, criterion.path, criterion.value);
    },
    deleteValueTransaction: function(state: FormStoreState, pathString: string): void {
        deleteValueByPath(state.criteriaTransaction, pathString);
    },
    deleteValueOrder: function(state: FormStoreState, pathString: string): void {
        deleteValueByPath(state.criteriaOrder, pathString);
    },
    setPublication: function(state : FormStoreState, publication: Publication): void{
        state.publication = publication;
    },
    setCopyFromId: function(state: FormStoreState, copyFromId: string): void {
        state.copyFromId = copyFromId;
    },
    setValidated: function(state, status: boolean): void {
        state.validated = status;
    }
};

const getters: GetterTree<FormStoreState, RootState> = {
    getPropertyType: (state: FormStoreState): string | null => {
        return state.criteria.property?.type;
    },
    getClassifiedId: (state: FormStoreState): number|null => {
        return state.classifiedId;
    },
    getFirstPublicationDate: (state: FormStoreState): string|null => {
        return state.firstPublicationDate;
    },
    editExpirationDate: (_state, getters): string => {
        const dateStr = getters.getFirstPublicationDate;
        if (!dateStr) return "";

        const dateObj = new Date(dateStr);
        if (isNaN(dateObj.getTime())) return "";

        // Add 7 days
        const plus7Days =  new Date(dateObj.getTime() + 7 * 24 * 60 * 60 * 1000);

        // Move to the next day (one day after plus7Days)
        const nextDay = new Date(plus7Days);
        nextDay.setDate(plus7Days.getDate() + 1);
        nextDay.setHours(0, 0, 0, 0);

        return formatDateDMY(nextDay);
    },
    editNotAllowed: (_state, getters): boolean => {
        const expStr = getters.editExpirationDate;
        if (!expStr) return false;

        // Parse expStr (assumed format DD-MM-YYYY)
        const [day, month, year] = expStr.split("-").map(Number);
        const expDate = new Date(year, month - 1, day);

        return Date.now() > expDate.getTime();
    },
    getlastSave: (state: FormStoreState): string|null => {
        return state.lastSave;
    },
    getErrorObjectByPath: (state: FormStoreState) => (path: string): string | null => {
        return getErrorObjectByPath(state.errors, path);
    },
    getCriteria: (state: FormStoreState): unknown => {
        return state.criteria;
    },
    getValue: (state: FormStoreState) => (path: string): string | null => {
        return getValueByPath(state.criteria, path) ;
    },
    getValueUI: (state: FormStoreState) => (path: string): string | null => {
        return getValueByPath(state.criteriaUI, path);
    },
    getValueTransaction: (state: FormStoreState) => (path: string): string | null => {
        return getValueByPath(state.criteriaTransaction, path);
    },
    getValueOrder: (state: FormStoreState) => (path: string): string | null => {
        return getValueByPath(state.criteriaOrder, path);
    },
    getIsEditionMode: (state: FormStoreState): boolean => {
        return state.isEditionMode;
    },
    getPublication: (state: FormStoreState): Publication => {
        return state.publication;
    },
    isReadonlyField: (state:FormStoreState, getters, rootState, rootGetters): boolean => {
        return state.isEditionMode && !rootGetters["UserModule/isMasquerading"];
    },
    getClassifiedCopyFromId: (state: FormStoreState): string|null => {
        return state.copyFromId;
    },
    getValidated: (state: FormStoreState): boolean => {
        return state.validated;
    },
    hasErrors: (state: FormStoreState): boolean => {
        return state.errors === null;
    }
};

const actions: ActionTree<FormStoreState, RootState> = {
    setPropertyType: function({commit, dispatch}, propertyType: string) {
        if(propertyType === "LAND") {
            dispatch("StepsWizard/remove", ["step-interior", "step-energy"], { root: true });
        }
        if(propertyType === "GARAGE") {
            dispatch("StepsWizard/remove", ["step-interior", "step-exterior"], { root: true });
        }
        commit("addValue", {"path": "property.type", "value": propertyType});
    },
    setPropertyTypeOptimizedFunnel: function({commit}, propertyType: string) {
        commit("addValue", {"path": "property.type", "value": propertyType});
    },
    setIsEditionMode: async function({commit, dispatch}, isEdition: boolean) {
        commit("setIsEditionMode", isEdition);
        if(isEdition) {
            dispatch("StepsWizard/init", { config:getSteps(),  isEditionMode: true }, { root:true });
            dispatch("StepsWizard/remove", [
                // TEMP: keep pictures step in edit funnel for testing — revert this
                // "step-pictures",
                "step-pricing",
                "step-payment",
            ], { root: true });
        }
    },
    addValue: function({commit, dispatch}, criterion: Criterion): void {
        commit("addValue", criterion);
        dispatch("updateErrors", criterion.path);
    },
    deleteValuePath: function({commit}, path: string) {
        commit("deleteValue", path);
    },
    deleteValueUIPath: function({commit}, path: string) {
        commit("deleteValueUI", path);
    },
    setCopyFromId: function({commit}, copyFromId) {
        commit("setCopyFromId", copyFromId);
    },
    validateAndGoToNextStep: function({dispatch}) {
        dispatch("goToNextStep");
    },
    goToNextStep: function({dispatch}) {
        dispatch("setCurrentStep", 1);
    },
    goToPreviousStep: function({dispatch}) {
        dispatch("setCurrentStep", -1);
    },
    setErrors: function({commit}, errors: ApiErrorWrapper) {
        commit("setValidated", errors === null);
        commit("setErrors", errors);
    },
    updateErrors: function({state, dispatch, commit, rootState}) {
        const newWarnings = state.errors?.warnings.filter(warning => {
            return !get(state.criteria, warning.property);
        });
        if(newWarnings && newWarnings.length === 0) {
            dispatch("setErrors", null);
        } else {
            commit("setErrorsWarning", newWarnings);
        }
        if(state.errors) {
            hasWarningsInCurrentStep(state.errors, rootState.StepsWizard.current.step);
        }
    },
    updateValidated: function({commit}, status) {
        commit("setValidated", status);
    },
    resetCriteria: function({commit}) {
        commit("resetCriteria");
        commit("OrdersModule/resetState",null, {root: true});
    }
};

const form: Module<FormStoreState, RootState> = {
    namespaced: true,
    state: state,
    mutations: mutations,
    actions: actions,
    getters: getters,
};

export default form;
